import React, { useCallback, useEffect, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { IconButton, Paper, Tooltip } from '@mui/material';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ContentCopy from '@mui/icons-material/ContentCopy';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import DragIndicator from '@mui/icons-material/DragIndicator';
import { useTranslate } from 'react-admin';
import { newBlockId } from './blockId';
import { BlockTypeSwitchButton } from './BlockTypeSwitch';

/**
 * Floating action bar at the left edge of the selected top-level block:
 * drag handle, type switch, move up/down, duplicate, delete (ported from Scilora).
 */
export const BlockActionBar: React.FC<{ editor: Editor }> = ({ editor }) => {
    const translate = useTranslate();
    const [position, setPosition] = useState<{ top: number; visible: boolean }>({ top: 0, visible: false });

    const updatePosition = useCallback(() => {
        const { $from } = editor.state.selection;
        if ($from.depth < 1) {
            setPosition({ top: 0, visible: false });
            return;
        }
        const nodePos = $from.before(1);
        const dom = editor.view.nodeDOM(nodePos);
        const editorEl = editor.view.dom.closest('.oa-block-editor-body');
        if (!dom || !(dom instanceof HTMLElement) || !editorEl) {
            setPosition({ top: 0, visible: false });
            return;
        }
        setPosition({ top: dom.getBoundingClientRect().top - editorEl.getBoundingClientRect().top, visible: true });
    }, [editor]);

    useEffect(() => {
        editor.on('selectionUpdate', updatePosition);
        editor.on('update', updatePosition);
        return () => {
            editor.off('selectionUpdate', updatePosition);
            editor.off('update', updatePosition);
        };
    }, [editor, updatePosition]);

    const info = useCallback(() => {
        const { $from } = editor.state.selection;
        if ($from.depth < 1) return null;
        const nodePos = $from.before(1);
        const node = editor.state.doc.nodeAt(nodePos);
        if (!node) return null;
        return { nodePos, index: $from.index(0), node };
    }, [editor]);

    const moveUp = () => {
        const i = info();
        if (!i || i.index === 0) return;
        let pos = 0;
        for (let k = 0; k < i.index - 1; k++) pos += editor.state.doc.child(k).nodeSize;
        const json = i.node.toJSON();
        editor.chain().focus().deleteRange({ from: i.nodePos, to: i.nodePos + i.node.nodeSize }).insertContentAt(pos, json).run();
    };

    const moveDown = () => {
        const i = info();
        if (!i || i.index >= editor.state.doc.childCount - 1) return;
        const next = editor.state.doc.child(i.index + 1);
        const json = i.node.toJSON();
        editor.chain().focus().deleteRange({ from: i.nodePos, to: i.nodePos + i.node.nodeSize }).run();
        editor.chain().focus().insertContentAt(i.nodePos + next.nodeSize, json).run();
    };

    const duplicate = () => {
        const i = info();
        if (!i) return;
        const json = i.node.toJSON();
        if (json.attrs) json.attrs.blockId = newBlockId();
        editor.chain().focus().insertContentAt(i.nodePos + i.node.nodeSize, json).run();
    };

    const remove = () => {
        const i = info();
        if (!i) return;
        editor.chain().focus().deleteRange({ from: i.nodePos, to: i.nodePos + i.node.nodeSize }).run();
    };

    if (!position.visible || !editor.isEditable) return null;
    const current = info();
    const canUp = current !== null && current.index > 0;
    const canDown = current !== null && current.index < editor.state.doc.childCount - 1;

    const btn = (title: string, onClick: () => void, icon: React.ReactNode, disabled = false) => (
        <Tooltip title={title} placement="left">
            <span>
                <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={onClick} disabled={disabled} aria-label={title} sx={{ color: 'var(--oa-text-2)' }}>
                    {icon}
                </IconButton>
            </span>
        </Tooltip>
    );

    return (
        <Paper elevation={0} className="oa-block-actionbar" style={{ top: position.top }} role="toolbar" aria-label={translate('blockEditor.actions.toolbar')}>
            <Tooltip title={translate('blockEditor.actions.drag')} placement="left">
                <span className="oa-block-draghandle" data-drag-handle draggable contentEditable={false}>
                    <DragIndicator fontSize="small" />
                </span>
            </Tooltip>
            <BlockTypeSwitchButton editor={editor} />
            {btn(translate('blockEditor.actions.moveUp'), moveUp, <ArrowUpward fontSize="inherit" />, !canUp)}
            {btn(translate('blockEditor.actions.moveDown'), moveDown, <ArrowDownward fontSize="inherit" />, !canDown)}
            {btn(translate('blockEditor.actions.duplicate'), duplicate, <ContentCopy fontSize="inherit" />)}
            {btn(translate('blockEditor.actions.delete'), remove, <DeleteOutline fontSize="inherit" />)}
        </Paper>
    );
};
